import {
  QuantitativeQueryMessageFeedback,
  QueryMessageFragmentFragment,
  QueryMessagePart,
  QueryMessageStatus,
} from '@sc/graphql';
import classNames from 'classnames';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { PluggableList } from 'unified';
import {
  FC,
  Fragment,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SourceCount } from './SourceCount';
import { CitationChip } from './CitationChip';
import { remarkCitations } from '../util/remarkCitations';
import { injectCitationTags } from '../util/injectCitationTags';
import { IconButton } from './IconButton';
import { IconButtonInverted } from './IconButtonInverted';
import { useTheme } from '../providers/ThemeContext';
import {
  ChevronDownIcon,
  ClipboardDocumentIcon,
  DocumentMagnifyingGlassIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
} from '@heroicons/react/24/outline';
import {
  HandThumbDownIcon as HandThumbDownIconSolid,
  HandThumbUpIcon as HandThumbUpIconSolid,
} from '@heroicons/react/24/solid';
import {
  useEventSource,
  EventSourceDataPayload,
} from '../hooks/useEventSource';
import { useApolloClient, useMutation } from '@apollo/client/react';
import {
  GQL_FRAGMENT_PROJECT_QUERY,
  GQL_FRAGMENT_QUERY_MESSAGE,
  GQL_MUTATION_UPDATE_QUERY_MESSAGE,
} from '../../queries';
import BrainIcon from '../icons/Brain.svg?react';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import {
  default as SyntaxHighlight,
  SyntaxHighlighterProps,
} from 'react-syntax-highlighter';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import ChatLoader from '../icons/ChatLoader.svg?react';
import ChatTyping from '../icons/ChatTyping.svg?react';

const SyntaxHighlighter =
  SyntaxHighlight as unknown as FC<SyntaxHighlighterProps>;

const HEX_COLOUR_REGEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const REMARK_PLUGINS: PluggableList = [remarkGfm, remarkCitations];

const STREAM_STAGES = ['citing', 'searching', 'writing'] as const;

type StreamStageType = (typeof STREAM_STAGES)[number];

interface Props {
  message: QueryMessageFragmentFragment;
  idx: number;
  arrLength: number;
  messageRetrievalEndpoint: string;
  queryUuid: string;
  onError: (error: unknown) => void;
}

export const PartialChatMessage = ({
  message,
  idx,
  arrLength,
  messageRetrievalEndpoint,
  queryUuid,
  onError,
}: Props) => {
  const client = useApolloClient();

  const { data, error } = useEventSource({
    url: `${messageRetrievalEndpoint}?token=${message.retrievalToken}`,
  });

  const handleDataCallback = useCallback(
    (data: EventSourceDataPayload[]) => {
      const parts = data
        .filter(
          (d): d is { name: 'message'; data: string } =>
            !!(d.name === 'message' && d.data)
        )
        .map((d) => JSON.parse(d.data) as QueryMessagePart);

      const groundingChunks = data
        .filter(
          (d): d is { name: 'grounding'; data: string } =>
            !!(d.name === 'grounding' && d.data)
        )
        .flatMap(
          (d) =>
            JSON.parse(
              d.data
            ) as QueryMessageFragmentFragment['groundingChunks']
        );

      const citations = data
        .filter(
          (d): d is { name: 'citations'; data: string } =>
            !!(d.name === 'citations' && d.data)
        )
        .flatMap(
          (d) => JSON.parse(d.data) as QueryMessageFragmentFragment['citations']
        );

      const isComplete = data.some((d) => d.name === 'completed');

      client.cache.updateFragment(
        {
          id: `QueryMessage:${message.uuid}`,
          fragment: GQL_FRAGMENT_QUERY_MESSAGE,
        },
        (d) =>
          d
            ? {
                ...d,
                parts: parts.map((p) => ({
                  thought: p.thought ?? null,
                  executableCode: p.executableCode ?? null,
                  text: p.text ?? null,
                })),
                groundingChunks,
                citations,
                status: isComplete ? QueryMessageStatus.completed : d.status,
              }
            : null
      );

      if (isComplete) {
        const completedEvent = JSON.parse(
          data.find(
            (d): d is { name: 'completed'; data: string } =>
              !!(d.name === 'completed' && d.data)
          )?.data ?? '{}'
        ) as { creditsConsumed: number };

        client.cache.updateFragment(
          {
            id: `ProjectQuery:${queryUuid}`,
            fragment: GQL_FRAGMENT_PROJECT_QUERY,
          },
          (d) =>
            d
              ? {
                  ...d,
                  rateLimit: {
                    ...d.rateLimit,
                    queriesConsumed:
                      d.rateLimit.queriesConsumed +
                      completedEvent.creditsConsumed,
                  },
                }
              : d
        );
      }
    },
    [client.cache, message.uuid, queryUuid]
  );

  useEffect(() => {
    if (data.length) {
      handleDataCallback(data);
    }
  }, [data, handleDataCallback]);

  if (error)
    return (
      <div className="text-red-400">
        <span className="text-sm">Err: {error}</span>
      </div>
    );

  const lastStageEvent = [...data]
    .reverse()
    .find((d) => d.name === 'stage' && d.data);

  const stageValue = lastStageEvent?.data
    ? (JSON.parse(lastStageEvent.data) as { stage?: string }).stage
    : undefined;

  const stage = STREAM_STAGES.find((s) => s === stageValue);

  return (
    <ChatMessage
      queryUuid={queryUuid}
      onError={onError}
      message={message}
      idx={idx}
      arrLength={arrLength}
      stage={stage}
    />
  );
};

const pullQuery = (input: string) => {
  const regex = /"(.*?)"/;
  const match = input.match(regex);

  return match ? match[1] : null;
};

const Thought = ({
  thoughts,
  currentBlock,
}: {
  thoughts: string[];
  currentBlock: boolean;
}) => {
  const splitThoughts = thoughts.map((text) => {
    const [title, content] = text.slice(2).split('**');

    return { title, content };
  });

  const currentThought = splitThoughts.at(-1);

  return (
    <Disclosure
      as="div"
      className="group border transition-all duration-200 border-grey-t-600 hover:bg-background-tertiary py-2 px-3 rounded-xs text-text-low-priority"
    >
      <DisclosureButton
        data-current={currentBlock}
        className="flex w-full items-center space-x-2 cursor-pointer data-[current=true]:animate-pulse transition-all duration-100 ease-out"
      >
        <BrainIcon className="size-4" />
        <span className="text-sm grow text-left overflow-hidden">
          {currentBlock ? (
            <span
              key={currentThought?.title}
              className="inline-block animate-slide-in"
            >
              {currentThought?.title}
            </span>
          ) : (
            `${thoughts.length} thoughts`
          )}
        </span>
        <ChevronDownIcon className="size-4 group-data-open:rotate-180 transition duration-200 ease-out" />
      </DisclosureButton>
      <div className="grid transition-all duration-200 ease-out group-data-[open]:grid-rows-[1fr] grid-rows-[0fr]">
        <DisclosurePanel static className="overflow-hidden space-y-3">
          {splitThoughts.map((th, idx) => (
            <div className="pt-3 space-y-1" key={idx}>
              <h6 className="text-h4 font-grotesque font-semibold">
                {th.title}
              </h6>
              <span className="text-sm">{th.content}</span>
            </div>
          ))}
        </DisclosurePanel>
      </div>
    </Disclosure>
  );
};

const Code = ({
  code,
  currentBlock,
}: {
  code: Array<QueryMessageFragmentFragment['parts'][number]['executableCode']>;
  currentBlock: boolean;
}) => {
  const codeStrings = code.map((c) => (c?.code ? pullQuery(c.code) : null));

  return codeStrings.map((c) =>
    c ? (
      <div
        data-current={currentBlock}
        key={c}
        className="flex w-full items-center space-x-2 border-grey-t-600 border text-text-low-priority py-2 px-3 rounded-xs data-[current=true]:animate-pulse overflow-hidden"
      >
        <DocumentMagnifyingGlassIcon className="size-4 shrink-0" />
        <span className="text-sm grow text-left truncate">
          File search: {c}
        </span>
      </div>
    ) : (
      <></>
    )
  );
};

export const ChatMessage = ({
  message,
  queryUuid,
  idx,
  arrLength,
  onError,
  stage,
}: Omit<Props, 'messageRetrievalEndpoint' | 'scrollToBottom'> & {
  stage?: StreamStageType | undefined;
}): ReactElement => {
  const { inverted, theme } = useTheme();

  const ThemedIconButton = inverted ? IconButtonInverted : IconButton;

  const loaderStyle = theme?.brandColour
    ? { color: theme.brandColour }
    : undefined;

  const [sourcesOpen, setSourcesOpen] = useState(false);

  const chunkByUuid = useMemo(
    () => new Map(message.groundingChunks.map((c) => [c.uuid, c])),
    [message.groundingChunks]
  );

  // One numbering shared by the chips and the Sources drawer — documents in
  // order of their first cited position in the answer.
  const citationNumberByDocUuid = useMemo(() => {
    const numbers = new Map<string, number>();

    [...message.citations]
      .sort(
        (a, b) =>
          (a.startIndex ?? Number.MAX_SAFE_INTEGER) -
          (b.startIndex ?? Number.MAX_SAFE_INTEGER)
      )
      .forEach((citation) => {
        const docUuid = citation.chunkId.split('#')[0];

        if (docUuid && !numbers.has(docUuid))
          numbers.set(docUuid, numbers.size + 1);
      });

    return numbers;
  }, [message.citations]);

  const blocks = useMemo(
    () =>
      message.parts.reduce<
        Array<{
          id: string;
          code: Array<
            QueryMessageFragmentFragment['parts'][number]['executableCode']
          > | null;
          thought: string[] | null;
          text: string[] | null;
        }>
      >((prev, curr) => {
        const elementToAdd = {
          id: String(Math.random() * 100),
          code: curr.executableCode ? [curr.executableCode] : null,
          thought: curr.thought && curr.text ? [curr.text] : null,
          text: !curr.thought && curr.text ? [curr.text] : null,
        };

        const keyWithData = Object.keys(elementToAdd).find(
          (k) => k !== 'id' && !!elementToAdd[k as keyof typeof elementToAdd]
        );

        if (keyWithData) {
          const lastAdded = prev.at(-1);
          const lastAddedKeyWithData = lastAdded
            ? Object.keys(lastAdded).find(
                (k) => k !== 'id' && !!lastAdded[k as keyof typeof lastAdded]
              )
            : undefined;

          if (lastAdded && keyWithData === lastAddedKeyWithData) {
            const newData = [
              ...(lastAdded[keyWithData as keyof typeof lastAdded] ?? []),
              ...(elementToAdd[keyWithData as keyof typeof elementToAdd] ?? []),
            ];

            prev.splice(-1);

            return [
              ...prev,
              {
                ...elementToAdd,
                [keyWithData]: newData,
              },
            ];
          }

          return [...prev, elementToAdd];
        }

        return prev;
      }, []),
    [message.parts]
  );

  const currentBlock = blocks.at(-1);

  const [updateQueryMessage] = useMutation(GQL_MUTATION_UPDATE_QUERY_MESSAGE, {
    onError,
    optimisticResponse: {
      updateQueryMessage: true,
    },
    update: (cache, _, context) =>
      cache.updateFragment(
        {
          id: `QueryMessage:${message.uuid}`,
          fragment: GQL_FRAGMENT_QUERY_MESSAGE,
        },
        (d) =>
          d
            ? {
                ...d,
                feedback: context.variables?.input.feedback
                  ? {
                      quantitative: context.variables.input.feedback,
                      qualitative: null,
                    }
                  : null,
              }
            : null
      ),
  });

  return (
    <div className="space-y-3 flex flex-col">
      {blocks.map((b, idx, arr) => (
        <Fragment key={b.id}>
          {b.code && (
            <Code code={b.code} currentBlock={idx === arr.length - 1} />
          )}
          {b.thought && (
            <Thought
              currentBlock={idx === arr.length - 1}
              thoughts={b.thought}
            />
          )}
          {b.text && (
            <div className="space-y-5 mt-5">
              <Markdown
                remarkPlugins={REMARK_PLUGINS}
                components={{
                  sup(props) {
                    const { node, children, ...rest } = props;

                    const attrs = rest as Record<string, unknown>;
                    const sourceId = attrs['data-citation-source-id'];
                    const number = attrs['data-citation-number'];

                    if (
                      typeof sourceId !== 'string' ||
                      typeof number !== 'string'
                    )
                      return <sup {...rest}>{children}</sup>;

                    const chunk = chunkByUuid.get(sourceId);

                    if (!chunk) return <></>;

                    return (
                      <CitationChip
                        number={
                          citationNumberByDocUuid.get(sourceId) ??
                          Number(number)
                        }
                        chunk={chunk}
                        onOpenSources={
                          message.status === QueryMessageStatus.completed &&
                          message.groundingChunks.length
                            ? () => setSourcesOpen(true)
                            : undefined
                        }
                      />
                    );
                  },
                  table(props) {
                    const { node, ...rest } = props;

                    return (
                      <div className="overflow-x-auto my-4">
                        <table
                          className="w-full border-collapse text-sm"
                          {...rest}
                        />
                      </div>
                    );
                  },
                  thead(props) {
                    const { node, ...rest } = props;

                    return (
                      <thead
                        className="border-b border-grey-t-600 text-left"
                        {...rest}
                      />
                    );
                  },
                  th(props) {
                    const { node, ...rest } = props;

                    return (
                      <th
                        className="px-3 py-2 font-medium align-top"
                        {...rest}
                      />
                    );
                  },
                  td(props) {
                    const { node, ...rest } = props;

                    return (
                      <td
                        className="px-3 py-2 border-t border-grey-t-600 align-top"
                        {...rest}
                      />
                    );
                  },
                  ul(props) {
                    const { node, ...rest } = props;

                    return (
                      <ul className="list-disc space-y-2 ml-8" {...rest} />
                    );
                  },
                  ol(props) {
                    const { node, ...rest } = props;

                    return (
                      <ol className="list-decimal space-y-2 ml-8" {...rest} />
                    );
                  },
                  strong(props) {
                    const { node, ...rest } = props;

                    return <strong className="font-medium" {...rest} />;
                  },
                  a(props) {
                    const { node, ...rest } = props;

                    return (
                      <a
                        className="underline underline-offset-2 break-all hover:opacity-80"
                        target="_blank"
                        rel="noopener noreferrer"
                        {...rest}
                      />
                    );
                  },
                  h2(props) {
                    const { node, ...rest } = props;

                    return (
                      <h2
                        className="text-h2 font-semibold font-grotesque"
                        {...rest}
                      />
                    );
                  },
                  h3(props) {
                    const { node, ...rest } = props;

                    return (
                      <h3
                        className="text-h3 font-semibold font-grotesque"
                        {...rest}
                      />
                    );
                  },
                  p(props) {
                    const { node, ...rest } = props;

                    return <p className="text-base" {...rest} />;
                  },
                  pre(props) {
                    const { node, ...rest } = props;

                    return (
                      <pre
                        className="overflow-x-auto rounded-lg flex my-4 w-full"
                        {...rest}
                      />
                    );
                  },
                  code(props) {
                    const { children, className, node, ...rest } = props;
                    const match =
                      /language-(\w+)/
                        .exec(className ?? '')?.[0]
                        ?.replace('language-', '') ?? 'markdown';

                    if (typeof children !== 'string')
                      return (
                        <span className="text-sm font-open">
                          Unable to render code block
                        </span>
                      );

                    const codeblock = String(children).replace(/\n$/, '');

                    if (!String(children).includes('\n')) {
                      const isColour = HEX_COLOUR_REGEX.test(codeblock);

                      return (
                        <code
                          className={classNames(
                            'bg-slate-800 mx-0.5 rounded-xs text-amber-300 px-1.5 py-0.5 text-sm font-mono',
                            {
                              'inline-flex items-center gap-1.5 align-middle':
                                isColour,
                            }
                          )}
                        >
                          {isColour && (
                            // Border keeps light swatches visible on light themes.
                            <span
                              className="size-3 rounded-xs border border-grey-t-600"
                              style={{ backgroundColor: codeblock }}
                            />
                          )}
                          {codeblock}
                        </code>
                      );
                    }

                    return (
                      <div className="p-1 rounded-lg bg-[#282c34] border border-grey-700 w-full">
                        <div className="flex items-center justify-between px-2 mt-1 text-text-low-priority">
                          <span className="text-sm font-open">{match}</span>
                          <IconButton
                            Icon={ClipboardDocumentIcon}
                            size="small"
                            iconClassName="stroke-text-low-priority!"
                            tooltip="Copy"
                            tooltipId={String(Math.random())}
                            onClick={() =>
                              void navigator.clipboard.writeText(codeblock)
                            }
                          />
                        </div>
                        <SyntaxHighlighter
                          {...rest}
                          PreTag="div"
                          wrapLongLines
                          language={match}
                          style={atomOneDark}
                        >
                          {codeblock}
                        </SyntaxHighlighter>
                      </div>
                    );
                  },
                }}
              >
                {injectCitationTags({
                  text: b.text.join(''),
                  citations: message.citations,
                })}
              </Markdown>
            </div>
          )}
        </Fragment>
      ))}
      {message.status === QueryMessageStatus.completed ? (
        <div
          className={classNames(
            'flex transition-all duration-75 items-center justify-between',
            {
              'opacity-0 group-hover:opacity-100': idx < arrLength - 1,
            }
          )}
        >
          <div>
            {!!message.groundingChunks.length && (
              <SourceCount
                sources={message.groundingChunks}
                queryUuid={queryUuid}
                message={{ createdAt: message.createdAt, uuid: message.uuid }}
                open={sourcesOpen}
                setOpen={setSourcesOpen}
                numberByDocUuid={citationNumberByDocUuid}
              />
            )}
          </div>
          <div className="flex items-center space-x-2">
            <ThemedIconButton
              type="basic"
              tooltip="This is what I was looking for!"
              size="medium"
              Icon={
                message.feedback?.quantitative ===
                QuantitativeQueryMessageFeedback.correct
                  ? HandThumbUpIconSolid
                  : HandThumbUpIcon
              }
              onClick={() =>
                void updateQueryMessage({
                  variables: {
                    input: {
                      uuid: message.uuid,
                      createdAt: message.createdAt,
                      queryUuid,
                      feedback: QuantitativeQueryMessageFeedback.correct,
                    },
                  },
                })
              }
            />
            <ThemedIconButton
              type="basic"
              tooltip="This does not answer my question."
              size="medium"
              Icon={
                message.feedback?.quantitative ===
                QuantitativeQueryMessageFeedback.incorrect
                  ? HandThumbDownIconSolid
                  : HandThumbDownIcon
              }
              onClick={() =>
                void updateQueryMessage({
                  variables: {
                    input: {
                      uuid: message.uuid,
                      createdAt: message.createdAt,
                      queryUuid,
                      feedback: QuantitativeQueryMessageFeedback.incorrect,
                    },
                  },
                })
              }
            />
          </div>
        </div>
      ) : stage === 'citing' ? (
        <div
          className={classNames(
            'flex items-center space-x-2 animate-pulse',
            theme ? '' : 'text-primary-400'
          )}
          style={loaderStyle}
        >
          <ChatLoader className="size-5 shrink-0" />
          <span
            className={classNames(`text-sm`, {
              'text-text-low-priority': !inverted,
              'text-black': inverted,
            })}
          >
            Linking citations...
          </span>
        </div>
      ) : currentBlock?.text ? (
        <ChatTyping
          className={theme ? 'size-7' : 'size-7 text-primary-400'}
          style={loaderStyle}
        />
      ) : (
        <div
          className={classNames(
            'flex items-center space-x-2 animate-pulse',
            theme ? '' : 'text-primary-400'
          )}
          style={loaderStyle}
        >
          <ChatLoader className="size-5 shrink-0" />
          <span
            className={classNames(`text-sm`, {
              'text-text-low-priority': !inverted,
              'text-black': inverted,
            })}
          >
            Searching...
          </span>
        </div>
      )}
    </div>
  );
};
